/**
 * ClientPackagePdf — klant-presentatie (geen offerte met line items).
 *
 * Layout:
 *   pagina 1: cover (titel, klantnaam, datum, foto-grid)
 *   pagina 2+: verhalende klant-tekst + optionele inhoud-samenvatting
 *
 * Branding: 2BMade kleuren (#1E2D3D, #E87C3E, #6B9DB5) op donkere achtergrond.
 * Font: Plus Jakarta Sans (zelf gehost in /public/fonts/) zodat we niet
 * afhankelijk zijn van fonts.gstatic.com tijdens PDF render.
 * Geen prijzen, geen tabellen, geen marges in deze PDF.
 */
import { Document, Font, Image, Page, StyleSheet, Text, View } from '@react-pdf/renderer';
import { formatDateNL } from '@/lib/pdf-brand';
import type { Client, GiftPackage, GiftPackageItem, Project } from '@/lib/types';

// ── Brand colors per tenant-spec (donker schema) ─────────────
const C = {
  primary: '#1E2D3D',
  secondary: '#E87C3E',
  accent: '#6B9DB5',
  surface: '#1A2B38',
  bg: '#0F1B24',
  text: '#F1F5F9',
  textMuted: '#94A3B8',
  divider: '#334155',
  white: '#FFFFFF',
};

// Custom-font fetching faalde herhaaldelijk (404/502 op CDN, worker-context
// resolve issues op self-hosted). Voor robuustheid blijven we op de built-in
// PDF-fonts (Helvetica = standaard, geen runtime fetch nodig). 2BMade-huisstijl
// blijft behouden via kleuren en layout. Geen Font.register meer hier.
const PDF_FONT = 'Helvetica';
Font.registerHyphenationCallback((word) => [word]);

const s = StyleSheet.create({
  // ── Cover page ─────────────────────────────────────────
  coverPage: {
    backgroundColor: C.bg,
    padding: 0,
    color: C.text,
    fontFamily: PDF_FONT,
  },
  coverHeader: {
    paddingTop: 60,
    paddingHorizontal: 50,
  },
  coverBrandLine: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  coverBrandTagline: {
    color: C.accent,
    fontSize: 9,
    textTransform: 'uppercase',
    letterSpacing: 2,
  },
  coverBrand: {
    color: C.white,
    fontSize: 22,
    fontWeight: 700,
    marginTop: 10,
  },
  coverAccentBar: {
    width: 80,
    height: 3,
    backgroundColor: C.secondary,
    marginTop: 8,
  },
  coverTitleBlock: {
    paddingHorizontal: 50,
    marginTop: 60,
  },
  coverLabel: {
    color: C.accent,
    fontSize: 10,
    textTransform: 'uppercase',
    letterSpacing: 2,
  },
  coverTitle: {
    color: C.white,
    fontSize: 32,
    fontWeight: 700,
    marginTop: 10,
    lineHeight: 1.2,
  },
  coverClient: {
    color: C.secondary,
    fontSize: 18,
    fontWeight: 600,
    marginTop: 24,
  },
  coverMeta: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 16,
  },
  coverMetaCol: {},
  coverMetaLabel: {
    color: C.textMuted,
    fontSize: 8,
    textTransform: 'uppercase',
    letterSpacing: 1.5,
  },
  coverMetaValue: {
    color: C.text,
    fontSize: 11,
    fontWeight: 600,
    marginTop: 3,
  },

  // ── Photo grid op cover ───────────────────────────────
  photoGridWrap: {
    paddingHorizontal: 50,
    marginTop: 36,
  },
  photoGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  photoSingle: {
    width: '100%',
    height: 320,
    borderRadius: 6,
    objectFit: 'cover' as const,
    backgroundColor: C.surface,
  },
  photoHalf: {
    width: '49%',
    height: 200,
    borderRadius: 6,
    objectFit: 'cover' as const,
    backgroundColor: C.surface,
  },
  photoQuarter: {
    width: '49%',
    height: 130,
    borderRadius: 6,
    objectFit: 'cover' as const,
    backgroundColor: C.surface,
  },
  photoPlaceholder: {
    width: '100%',
    height: 200,
    borderRadius: 6,
    backgroundColor: C.surface,
    borderWidth: 1,
    borderColor: C.divider,
    borderStyle: 'dashed',
    justifyContent: 'center',
    alignItems: 'center',
  },
  photoPlaceholderText: {
    color: C.textMuted,
    fontSize: 10,
  },

  // ── Cover footer ───────────────────────────────────────
  coverFooter: {
    position: 'absolute',
    bottom: 32,
    left: 50,
    right: 50,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  coverFooterText: {
    color: C.textMuted,
    fontSize: 8,
  },
  coverFooterPowered: {
    color: C.accent,
    fontSize: 8,
    fontWeight: 600,
  },

  // ── Inner content page ─────────────────────────────────
  contentPage: {
    backgroundColor: C.bg,
    paddingTop: 50,
    paddingBottom: 70,
    paddingHorizontal: 50,
    color: C.text,
    fontFamily: PDF_FONT,
    fontSize: 11,
  },
  contentHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: C.divider,
  },
  contentBrand: {
    color: C.white,
    fontSize: 14,
    fontWeight: 700,
  },
  contentDocLabel: {
    color: C.accent,
    fontSize: 8,
    textTransform: 'uppercase',
    letterSpacing: 1.5,
  },
  sectionLabel: {
    color: C.accent,
    fontSize: 9,
    textTransform: 'uppercase',
    letterSpacing: 2,
    marginTop: 30,
    marginBottom: 12,
  },
  presentationText: {
    color: C.text,
    fontSize: 12,
    lineHeight: 1.7,
  },
  presentationParagraph: {
    marginBottom: 12,
  },
  contentBlock: {
    marginTop: 30,
    padding: 16,
    backgroundColor: C.surface,
    borderRadius: 6,
    borderLeftWidth: 3,
    borderLeftColor: C.secondary,
  },
  contentBlockTitle: {
    color: C.white,
    fontSize: 12,
    fontWeight: 700,
    marginBottom: 8,
  },
  contentBlockText: {
    color: C.text,
    fontSize: 10,
    lineHeight: 1.6,
  },
  contentBlockItem: {
    flexDirection: 'row',
    gap: 8,
    paddingVertical: 3,
  },
  contentBlockBullet: {
    color: C.secondary,
    fontSize: 10,
    fontWeight: 700,
    width: 10,
  },
  contentBlockItemText: {
    color: C.text,
    fontSize: 10,
    flex: 1,
    lineHeight: 1.5,
  },
  footer: {
    position: 'absolute',
    bottom: 28,
    left: 50,
    right: 50,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    color: C.textMuted,
    fontSize: 8,
  },
  footerLeft: {
    color: C.accent,
    fontSize: 8,
    fontWeight: 600,
  },
});

interface Props {
  project: Project;
  pkg: GiftPackage;
  client: Client | null;
}

// ── Helpers ──────────────────────────────────────────────

function splitParagraphs(text: string): string[] {
  return text
    .split(/\n{2,}/)
    .map((p) => p.replace(/\n/g, ' ').trim())
    .filter(Boolean);
}

function buildContentSummary(items: GiftPackageItem[]): string[] {
  // Korte verhalende lijst voor "Dit pakket bevat:" sectie als fallback/aanvulling.
  const lines: string[] = [];
  for (const it of items) {
    if (it.excluded) continue;
    const p = it.gift_product;
    if (!p) continue;
    const parts: string[] = [];
    if (it.quantity_per_package > 1) parts.push(`${it.quantity_per_package}x`);
    parts.push(p.name);
    if (p.brand && p.brand !== p.name) parts.push(`(${p.brand})`);
    lines.push(parts.join(' '));
  }
  return lines;
}

function PhotoGrid({ urls }: { urls: string[] }) {
  const safeUrls = urls.filter(Boolean).slice(0, 4);
  if (safeUrls.length === 0) {
    return (
      <View style={s.photoPlaceholder}>
        <Text style={s.photoPlaceholderText}>Nog geen foto's toegevoegd</Text>
      </View>
    );
  }
  if (safeUrls.length === 1) {
    return <Image src={safeUrls[0]} style={s.photoSingle} />;
  }
  if (safeUrls.length === 2) {
    return (
      <View style={s.photoGrid}>
        {safeUrls.map((url, i) => (
          <Image key={i} src={url} style={s.photoHalf} />
        ))}
      </View>
    );
  }
  // 3 of 4: 2x2 grid (bij 3 blijft één cel leeg)
  return (
    <View style={s.photoGrid}>
      {safeUrls.map((url, i) => (
        <Image key={i} src={url} style={s.photoQuarter} />
      ))}
    </View>
  );
}

export function ClientPackagePdf({ project, pkg, client }: Props) {
  const photoUrls = pkg.photo_urls ?? [];
  const presentation = (project.client_presentation_text ?? '').trim();
  const paragraphs = presentation ? splitParagraphs(presentation) : [];
  const contentList = buildContentSummary(pkg.items ?? []);
  const today = formatDateNL(new Date());

  return (
    <Document>
      {/* ── Pagina 1: Cover ─────────────────────────────────────── */}
      <Page size="A4" style={s.coverPage}>
        <View style={s.coverHeader}>
          <Text style={s.coverBrandTagline}>Relatiegeschenken</Text>
          <Text style={s.coverBrand}>2BMade</Text>
          <View style={s.coverAccentBar} />
        </View>

        <View style={s.coverTitleBlock}>
          <Text style={s.coverLabel}>Pakket-presentatie</Text>
          <Text style={s.coverTitle}>{pkg.name || project.name}</Text>
          {client?.company_name && <Text style={s.coverClient}>voor {client.company_name}</Text>}
          <View style={s.coverMeta}>
            <View style={s.coverMetaCol}>
              <Text style={s.coverMetaLabel}>Aantal pakketten</Text>
              <Text style={s.coverMetaValue}>{pkg.quantity}</Text>
            </View>
            {pkg.theme && (
              <View style={s.coverMetaCol}>
                <Text style={s.coverMetaLabel}>Thema</Text>
                <Text style={s.coverMetaValue}>{pkg.theme}</Text>
              </View>
            )}
            <View style={s.coverMetaCol}>
              <Text style={s.coverMetaLabel}>Datum</Text>
              <Text style={s.coverMetaValue}>{today}</Text>
            </View>
          </View>
        </View>

        <View style={s.photoGridWrap}>
          <PhotoGrid urls={photoUrls} />
        </View>

        <View style={s.coverFooter}>
          <Text style={s.coverFooterText}>2BMade · Leeuwarden · info@2bmade.nl</Text>
          <Text style={s.coverFooterPowered}>Powered by GrowthAI</Text>
        </View>
      </Page>

      {/* ── Pagina 2+: Verhalende tekst + inhoud-samenvatting ──── */}
      <Page size="A4" style={s.contentPage}>
        <View style={s.contentHeader}>
          <Text style={s.contentBrand}>2BMade</Text>
          <Text style={s.contentDocLabel}>{pkg.name || project.name}</Text>
        </View>

        <Text style={s.sectionLabel}>Naar aanleiding van ons gesprek</Text>

        {paragraphs.length > 0 ? (
          paragraphs.map((p, i) => (
            <Text key={i} style={[s.presentationText, s.presentationParagraph]}>
              {p}
            </Text>
          ))
        ) : (
          <Text style={[s.presentationText, s.presentationParagraph]}>
            (Nog geen klant-tekst ingevuld. Mark kan deze genereren via de "Genereer met AI" knop in
            de pakket-app.)
          </Text>
        )}

        {contentList.length > 0 && (
          <View style={s.contentBlock}>
            <Text style={s.contentBlockTitle}>Dit pakket bevat</Text>
            {contentList.map((line, i) => (
              <View key={i} style={s.contentBlockItem}>
                <Text style={s.contentBlockBullet}>·</Text>
                <Text style={s.contentBlockItemText}>{line}</Text>
              </View>
            ))}
          </View>
        )}

        <View style={s.footer} fixed>
          <Text style={s.footerLeft}>Powered by GrowthAI</Text>
          <Text>2BMade · info@2bmade.nl · +31 58 201 00 35</Text>
          <Text render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`} />
        </View>
      </Page>
    </Document>
  );
}
